// src/utils/panMessageGenerator.js
/**
 * Pan Message Generator
 * 
 * Smart message selection based on:
 * - Time remaining
 * - Performance (accuracy, streaks)
 * - Context (idle, struggling, excelling)
 * - User sentiment
 * 
 * Message Types:
 * - welcome: Session start
 * - encouragement: General positive reinforcement
 * - warning: Time running low
 * - streak: Achievement unlocked
 * - milestone: Time/note milestones
 * - idle: User hasn't played in a while
 * - correction: After wrong notes
 * - celebration: High performance
 */

const PAN_MESSAGES = {
  welcome: {
    free: [
      "Ready to practice? Let's go! 🎹",
      "Pan is here to help you shine! ✨",
      "Time to save Treble Town! 🐼",
      "Let's make some music together! 🎵",
    ],
    premium: [
      "Unlimited practice! Let's go! 🌟",
      "You're a Piano superstar! Ready? 🎹",
      "Pan is so proud of you! Let's play! 🐼",
    ],
  },
  
  encouragement: [
    "You're doing great! 🐼",
    "Keep going! Pan believes in you! 💪",
    "One note at a time! You've got this! 🎵",
    "Amazing effort! 🌟",
    "Pan is cheering for you! 📣",
    "You're getting better every day! 🚀",
  ],
  
  warning: {
    120: [ // 2 minutes
      "2 minutes left today! You're doing great! ⏰",
      "2 minutes remaining! Keep practicing! 🎵",
    ],
    60: [ // 1 minute
      "1 minute left! Almost there! 🔥",
      "60 seconds! Save Treble Town fast! 🐉",
      "Final minute! You can do it! ⚡",
    ],
    30: [ // 30 seconds
      "30 seconds! Finish this level! 🔥",
      "Half a minute! Make it count! ⏰",
      "30 seconds left! Pan is proud! 🐼",
    ],
  },
  
  streak: {
    5: [
      "5 in a row! You're on fire! 🔥",
      "Streak of 5! Amazing! 🌟",
      "5 perfect notes! Unstoppable! 🚀",
    ],
    10: [
      "10 streak! You're a legend! 🏆",
      "10 in a row! Pan is amazed! 🤩",
      "Incredible! 10 perfect notes! ⭐",
    ],
    20: [
      "20 STREAK! You're a Piano master! 👑",
      "TWENTY in a row! Unbelievable! 🎯",
      "20 perfect! Pan can't believe it! 🐼✨",
    ],
  },
  
  milestone: {
    time: {
      30: [
        "30 seconds! Great start! 🎵",
        "Half a minute in! Keep going! 🐼",
      ],
      60: [
        "1 minute! You're doing amazing! 🌟",
        "60 seconds of practice! Excellent! ⏱️",
      ],
      120: [
        "2 minutes! You're on a roll! 🔥",
        "Wow, 2 minutes already! 🚀",
      ],
      300: [
        "5 minutes! You're a Piano star! ⭐",
        "5 whole minutes! Pan is so proud! 🐼",
      ],
    },
    notes: {
      10: [
        "10 notes! Nice work! 🎵",
        "You've played 10 notes! Keep it up! 🐼",
      ],
      25: [
        "25 notes! You're crushing it! 🔥",
        "A quarter-century of notes! 🌟",
      ],
      50: [
        "50 notes! Halfway to 100! 🚀",
        "Half a hundred! Amazing! ⭐",
      ],
      100: [
        "100 NOTES! You're incredible! 🏆",
        "A HUNDRED notes! Pan is amazed! 🤩",
      ],
    },
  },
  
  idle: {
    10: [
      "Take your time! Pan believes in you! 💪",
      "No rush! You can do this! 🐼",
      "Thinking it through? That's smart! 🧠",
    ],
    30: [
      "Need a break? Pan will wait! 🐼",
      "Take all the time you need! 🎵",
      "Rest a moment if you need to! ☺️",
    ],
  },
  
  correction: [
    "That's okay! Try again! 🎵",
    "Almost! One more time! 🐼",
    "No worries! You'll get it! 💪",
    "Oops! Let's try that note again! 🎹",
    "Close! Pan knows you can do it! 🌟",
  ],
  
  celebration: {
    highAccuracy: [ // 95%+
      "Perfect playing! You're a natural! 💯",
      "Almost flawless! Amazing! ✨",
      "Incredible accuracy! Pan is proud! 🏆",
    ],
    fastCompletion: [
      "Lightning fast! Well done! ⚡",
      "You're speedy! Great job! 🚀",
      "Wow, that was quick! 🔥",
    ],
  },
  
  timeExpired: [
    "Time's up for today! Great practice! 🐼",
    "Out of time! Come back tomorrow! 🌅",
    "Daily practice complete! See you tomorrow! 👋",
  ],
  
  levelComplete: [
    "Level complete! You're amazing! 🎉",
    "You did it! On to the next one! 🚀",
    "Brilliant! Doron is getting worried! 🐉",
    "Perfect! Treble Town is safer! 🏰",
  ],
};

/**
 * Get random message from array
 */
function getRandomMessage(messages) {
  if (!Array.isArray(messages) || messages.length === 0) {
    return "Keep going! 🐼";
  }
  return messages[Math.floor(Math.random() * messages.length)];
}

/**
 * Generate welcome message
 */
function getWelcomeMessage(isPremium = false) {
  const messages = isPremium ? PAN_MESSAGES.welcome.premium : PAN_MESSAGES.welcome.free;
  return getRandomMessage(messages);
}

/**
 * Generate encouragement message
 */
function getEncouragementMessage() {
  return getRandomMessage(PAN_MESSAGES.encouragement);
}

/**
 * Generate time warning message
 */
function getWarningMessage(secondsRemaining) {
  let threshold = 30;
  if (secondsRemaining > 30 && secondsRemaining <= 60) {
    threshold = 60;
  } else if (secondsRemaining > 60 && secondsRemaining <= 120) {
    threshold = 120;
  }
  
  return getRandomMessage(PAN_MESSAGES.warning[threshold]);
}

/**
 * Generate streak message
 */
function getStreakMessage(streakCount) {
  let threshold = 5;
  if (streakCount >= 20) {
    threshold = 20;
  } else if (streakCount >= 10) {
    threshold = 10;
  }
  
  return getRandomMessage(PAN_MESSAGES.streak[threshold]);
}

/**
 * Generate time milestone message
 */
function getTimeMilestoneMessage(secondsElapsed) {
  let threshold = 30;
  if (secondsElapsed >= 300) threshold = 300;
  else if (secondsElapsed >= 120) threshold = 120;
  else if (secondsElapsed >= 60) threshold = 60;
  
  return getRandomMessage(PAN_MESSAGES.milestone.time[threshold]);
}

/**
 * Generate note milestone message
 */
function getNoteMilestoneMessage(notesPlayed) {
  let threshold = 10;
  if (notesPlayed >= 100) threshold = 100;
  else if (notesPlayed >= 50) threshold = 50;
  else if (notesPlayed >= 25) threshold = 25;
  
  return getRandomMessage(PAN_MESSAGES.milestone.notes[threshold]);
}

/**
 * Generate idle message
 */
function getIdleMessage(idleSeconds) {
  const threshold = idleSeconds >= 30 ? 30 : 10;
  return getRandomMessage(PAN_MESSAGES.idle[threshold]);
}

/**
 * Generate correction message
 */
function getCorrectionMessage() {
  return getRandomMessage(PAN_MESSAGES.correction);
}

/**
 * Generate celebration message
 */
function getCelebrationMessage(type = 'highAccuracy') {
  return getRandomMessage(PAN_MESSAGES.celebration[type] || PAN_MESSAGES.celebration.highAccuracy);
}

/**
 * Generate time expired message
 */
function getTimeExpiredMessage() {
  return getRandomMessage(PAN_MESSAGES.timeExpired);
}

/**
 * Generate level complete message
 */
function getLevelCompleteMessage() {
  return getRandomMessage(PAN_MESSAGES.levelComplete);
}

/**
 * Smart message selector based on context
 * 
 * @param {Object} context - Current practice context
 * @returns {string} - Selected message
 */
function getSmartMessage(context = {}) {
  const {
    type = 'encouragement',
    isPremium = false,
    secondsRemaining = 0,
    streakCount = 0,
    secondsElapsed = 0,
    notesPlayed = 0,
    idleSeconds = 0,
    accuracy = 0,
  } = context;
  
  switch (type) {
    case 'welcome':
      return getWelcomeMessage(isPremium);
    
    case 'warning':
      return getWarningMessage(secondsRemaining);
    
    case 'streak':
      return getStreakMessage(streakCount);
    
    case 'timeMilestone':
      return getTimeMilestoneMessage(secondsElapsed);
    
    case 'noteMilestone':
      return getNoteMilestoneMessage(notesPlayed);
    
    case 'idle':
      return getIdleMessage(idleSeconds);
    
    case 'correction':
      return getCorrectionMessage();
    
    case 'celebration':
      if (accuracy >= 95) {
        return getCelebrationMessage('highAccuracy');
      }
      return getCelebrationMessage('fastCompletion');
    
    case 'timeExpired':
      return getTimeExpiredMessage();
    
    case 'levelComplete':
      return getLevelCompleteMessage();
    
    case 'encouragement':
    default:
      return getEncouragementMessage();
  }
}

/**
 * Message priority system
 * Higher priority messages override lower ones
 */
const MESSAGE_PRIORITY = {
  timeExpired: 100,
  warning: 90,
  levelComplete: 80,
  streak: 70,
  celebration: 60,
  milestone: 50,
  correction: 40,
  idle: 30,
  encouragement: 20,
  welcome: 10,
};

/**
 * Should show message based on priority and cooldown
 */
function shouldShowMessage(newType, currentType, lastMessageTime, cooldownMs = 3000) {
  // Always show time expired and warnings
  if (newType === 'timeExpired' || newType === 'warning') {
    return true;
  }
  
  // Check priority
  const newPriority = MESSAGE_PRIORITY[newType] || 0;
  const currentPriority = MESSAGE_PRIORITY[currentType] || 0;
  
  if (newPriority <= currentPriority) {
    return false;
  }
  
  // Check cooldown
  if (lastMessageTime && Date.now() - lastMessageTime < cooldownMs) {
    return false;
  }
  
  return true;
}

// Export all functions as a single object
const panMessageGenerator = {
  getSmartMessage,
  getWelcomeMessage,
  getEncouragementMessage,
  getWarningMessage,
  getStreakMessage,
  getTimeMilestoneMessage,
  getNoteMilestoneMessage,
  getIdleMessage,
  getCorrectionMessage,
  getCelebrationMessage,
  getTimeExpiredMessage,
  getLevelCompleteMessage,
  shouldShowMessage,
  MESSAGE_PRIORITY,
};

// Re-export individual functions as named exports (for direct imports)
export {
  getSmartMessage,
  getWelcomeMessage,
  getEncouragementMessage,
  getWarningMessage,
  getStreakMessage,
  getTimeMilestoneMessage,
  getNoteMilestoneMessage,
  getIdleMessage,
  getCorrectionMessage,
  getCelebrationMessage,
  getTimeExpiredMessage,
  getLevelCompleteMessage,
  shouldShowMessage,
  MESSAGE_PRIORITY,
};

// Default export (for importing the entire module)
export default panMessageGenerator;